// Applying a Figma-API plugin's recorded changes to a document (see figmaShim.ts). Kept free of
// the app so the same code runs headless (the Figma parity test uses it).

import { flattenPath, parseSvgPath, pathToSvg, transformPath, makeNode, newId, instantiate, booleanOperation, flatten, componentSetOf, variantOptions, setVariant, swapInstance, combineAsVariants, type Editor, type NodeData, type NodeId, type Op, type Paint, type VariableValue } from '@vellum/core';

export interface FigmaOpHooks {
  /** figma.createNodeFromSvg */
  importSvg?: (svg: string) => NodeId | null;
  toast?: (msg: string) => void;
}

export type FigmaOp =
  | { op: 'varCollection'; id: NodeId; name: string }
  | { op: 'var'; id: NodeId; name: string; collectionId: NodeId; type: 'COLOR' | 'FLOAT' | 'STRING' | 'BOOLEAN' }
  | { op: 'varValue'; id: NodeId; modeId: string; value: VariableValue }
  | { op: 'varRename'; id: NodeId; name: string }
  | { op: 'addMode'; id: NodeId; name: string }
  | { op: 'bind'; id: NodeId; field: string; varId: NodeId | null }
  | { op: 'instance'; id: NodeId; componentId: NodeId }
  | { op: 'detach'; id: NodeId }
  | { op: 'group'; id: NodeId; ids: NodeId[] }
  | { op: 'style'; id: NodeId; styleType: 'FILL' | 'TEXT' | 'EFFECT' | 'GRID' }
  | { op: 'styleSet'; id: NodeId; props: Partial<NodeData> }
  | { op: 'boolean'; id: NodeId; ids: NodeId[]; kind: 'UNION' | 'SUBTRACT' | 'INTERSECT' | 'EXCLUDE' | 'FLATTEN'; fills?: Paint[] }
  | { op: 'create'; node: NodeData }
  | { op: 'set'; id: NodeId; props: Partial<NodeData> }
  | { op: 'move'; id: NodeId; parent: NodeId; at: number }
  | { op: 'delete'; id: NodeId }
  | { op: 'clone'; id: NodeId; newId: NodeId }
  | { op: 'page'; id: NodeId }
  | { op: 'flows'; flows: { nodeId: NodeId; name: string }[] }
  | { op: 'svg'; id: NodeId; svg: string }
  | { op: 'toComponent'; id: NodeId }
  | { op: 'combine'; id: NodeId; ids: NodeId[] }
  | { op: 'ungroup'; id: NodeId }
  | { op: 'setProps'; id: NodeId; values: Record<string, string | boolean> }
  | { op: 'swap'; id: NodeId; componentId: NodeId }
  | { op: 'resetOverrides'; id: NodeId };

// fields the shim may set; anything else from plugin code is ignored
const FIGMA_PROPS = new Set(['name', 'exposedInstance', 'gridStyleId', 'reactions', 'variableModes', 'exportSettings', 'relaunchData', 'strokeAlign', 'strokeCap', 'strokeJoin', 'strokeDash', 'blendMode', 'isMask', 'clipsContent', 'description', 'constraintH', 'constraintV', 'annotations', 'layoutWrap', 'layoutPositioning', 'counterAxisSpacing', 'minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'layoutGrids', 'textCase', 'textDecoration', 'letterSpacing', 'lineHeight', 'textRuns', 'pluginData', 'componentProps', 'propRefs', 'shape', 'connectorStart', 'connectorEnd', 'connectorType', 'startCap', 'endCap', 'cornerSmoothing', 'strokeWeights', 'scopes', 'codeSyntax', 'x', 'y', 'width', 'height', 'rotation', 'opacity', 'visible', 'locked', 'fills', 'strokes', 'strokeWeight', 'cornerRadius', 'characters', 'fontSize', 'fontFamily', 'fontWeight', 'italic', 'textAlign', 'layoutMode', 'itemSpacing', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft', 'primaryAxisAlign', 'counterAxisAlign', 'layoutSizingH', 'layoutSizingV', 'textAutoResize', 'path', 'windingRule', 'effects', 'strokeWidthProfile', 'strokeDynamic', 'strokeBrush', 'fillStyleId', 'strokeStyleId', 'textStyleId', 'effectStyleId', 'pointCount', 'innerRadius', 'arcData', 'strokesIncludedInLayout', 'textColor', 'authorName', 'textTruncation', 'maxLines', 'videoPlayback']);
const STYLE_PROPS = new Set(['name', 'fills', 'effects', 'fontSize', 'fontFamily', 'fontWeight', 'italic', 'lineHeight', 'layoutGrids']);
const FIGMA_TYPES = new Set(['RECTANGLE', 'ELLIPSE', 'LINE', 'POLYGON', 'STAR', 'TEXT', 'FRAME', 'COMPONENT', 'STICKY', 'VECTOR', 'SECTION', 'SHAPE_WITH_TEXT', 'CONNECTOR']);

function pick(props: Partial<NodeData>) {
  return Object.fromEntries(Object.entries(props).filter(([k]) => FIGMA_PROPS.has(k))) as Partial<NodeData>;
}

/** Image paints refer to plugin-side hashes until the bytes are uploaded. */
export function resolveImages<T extends Partial<NodeData>>(props: T, urls: Map<string, string>): T {
  if (!props.fills) return props;
  return { ...props, fills: props.fills.map((p) => (p.type === 'IMAGE' && urls.has(p.src) ? { ...p, src: urls.get(p.src)! } : p)) };
}

export function applyFigmaOps(ed: Editor, ops: FigmaOp[], urls: Map<string, string> = new Map(), idMap: Record<string, string> = {}, hooks: FigmaOpHooks = {}) {
  const doc = ed.doc;
  const real = (id: NodeId) => idMap[id] ?? id;
  for (const raw of ops) {
    // ids the host replaced (boolean results) carry through later ops
    const o = { ...raw, ...('id' in raw ? { id: real(raw.id) } : {}), ...('ids' in raw ? { ids: raw.ids.map(real) } : {}) } as FigmaOp;
    if (o.op === 'create') o.node = resolveImages(o.node, urls);
    if (o.op === 'set') o.props = resolveImages(o.props, urls);
    if (o.op === 'style') {
      ed.createStyle(o.styleType, o.styleType === 'FILL' ? 'Paint style' : o.styleType === 'TEXT' ? 'Text style' : o.styleType === 'GRID' ? 'Grid style' : 'Effect style', { id: o.id } as NodeData);
      continue;
    }
    if (o.op === 'styleSet') {
      if (doc.get(o.id)?.type === 'STYLE') ed.update(o.id, resolveImages(Object.fromEntries(Object.entries(o.props).filter(([k]) => STYLE_PROPS.has(k))) as Partial<NodeData>, urls), 'Edit style');
      continue;
    }
    if (o.op === 'boolean') {
      const ids = o.ids.filter((id) => doc.has(id));
      if (!ids.length) continue;
      const made = o.kind === 'FLATTEN' ? flatten(ed, ids)[0] : booleanOperation(ed, ids, o.kind);
      if (made) {
        idMap[(raw as { id: NodeId }).id] = made;
        if (o.fills && o.kind !== 'FLATTEN') ed.update(made, { fills: o.fills }, 'Plugin edit');
      }
      continue;
    }
    if (o.op === 'varCollection') ed.createVariableCollection(o.name, ['Mode 1'], o.id);
    else if (o.op === 'var') {
      if (doc.get(o.collectionId)?.type === 'VARIABLE_COLLECTION') ed.createVariable(o.collectionId, o.name, o.type, {}, o.id);
    } else if (o.op === 'varValue') {
      if (doc.get(o.id)?.type === 'VARIABLE') ed.setVariableValue(o.id, o.modeId, o.value);
    } else if (o.op === 'varRename') {
      if (doc.get(o.id)?.type === 'VARIABLE') ed.update(o.id, { name: o.name });
    } else if (o.op === 'addMode') {
      if (doc.get(o.id)?.type === 'VARIABLE_COLLECTION') ed.addMode(o.id, o.name);
    } else if (o.op === 'bind') {
      if (!doc.has(o.id)) continue;
      try {
        ed.bindVariable([o.id], o.field, o.varId && doc.has(o.varId) ? o.varId : null);
      } catch {
        hooks.toast?.(`This plugin bound “${o.field}”, which can't be bound here`);
      }
    } else if (o.op === 'instance') {
      if (doc.get(o.componentId)?.type !== 'COMPONENT') continue;
      const c = doc.must(o.componentId);
      ed.applyOps(instantiate(doc, o.componentId, ed.pageId, doc.indexAt(ed.pageId), { x: c.x, y: c.y }, o.id).ops);
    } else if (o.op === 'detach') {
      if (doc.get(o.id)?.type === 'INSTANCE') ed.detachInstance(o.id);
    } else if (o.op === 'group') {
      const ids = o.ids.filter((id) => doc.has(id));
      if (ids.length) ed.wrap(ids, 'GROUP', { id: o.id, name: 'Group' });
    } else if (o.op === 'create') {
      if (!FIGMA_TYPES.has(o.node.type)) continue;
      const parent = o.node.parent && doc.has(o.node.parent) ? o.node.parent : ed.pageId;
      ed.applyOps([{ op: 'create', node: makeNode(o.node.type, { ...pick(o.node), id: o.node.id, parent, index: doc.indexAt(parent) }) }]);
    } else if (o.op === 'set') {
      if (!doc.has(o.id)) continue;
      const props = pick(o.props);
      // vectorPaths: like Figma, the box fits the path's curves and the path starts at its corner
      if (props.path !== undefined && doc.get(o.id)!.type === 'VECTOR') Object.assign(props, fitVectorPath(props.path));
      ed.update(o.id, props, 'Plugin edit');
    } else if (o.op === 'move') {
      if (doc.has(o.id) && (doc.has(o.parent) || o.parent === ed.pageId) && !doc.isAncestor(o.id, o.parent)) ed.applyOps([{ op: 'set', id: o.id, props: { parent: o.parent, index: doc.indexAt(o.parent, o.at) } }]);
    } else if (o.op === 'delete') {
      if (doc.has(o.id)) ed.remove([o.id]);
    } else if (o.op === 'flows') {
      // ids made earlier in this batch arrive under their plugin-side names
      const flows = o.flows.map((f) => ({ ...f, nodeId: idMap[f.nodeId] ?? f.nodeId })).filter((f) => doc.has(f.nodeId));
      ed.update(ed.pageId, { flowStartingPoints: flows.length ? flows : undefined, prototypeStartId: undefined }, 'Plugin flows');
    } else if (o.op === 'page') {
      idMap[(raw as { id: NodeId }).id] = ed.addPage();
    } else if (o.op === 'svg') {
      const made = hooks.importSvg?.(o.svg) ?? null;
      if (made) idMap[(raw as { id: NodeId }).id] = made;
    } else if (o.op === 'toComponent') {
      if (doc.has(o.id)) {
        const made = ed.createComponent([o.id]);
        if (made !== o.id) idMap[(raw as { id: NodeId }).id] = made;
      }
    } else if (o.op === 'combine') {
      const ids = o.ids.filter((id) => doc.get(id)?.type === 'COMPONENT');
      if (ids.length) idMap[(raw as { id: NodeId }).id] = combineAsVariants(ed, ids);
    } else if (o.op === 'ungroup') {
      if (doc.has(o.id)) ed.ungroup([o.id]);
    } else if (o.op === 'setProps') {
      if (doc.get(o.id)?.type !== 'INSTANCE') continue;
      // variant properties switch the variant; the rest are component properties
      const inst = doc.must(o.id);
      const set = componentSetOf(doc, inst.componentId!);
      const variants = set ? variantOptions(doc, set.id) : {};
      const v: Record<string, string> = {},
        props: Record<string, string | boolean> = {};
      for (const [k, val] of Object.entries(o.values)) if (k in variants) v[k] = String(val);
      else props[k] = val;
      if (Object.keys(v).length) setVariant(ed, o.id, v);
      if (Object.keys(props).length) {
        try {
          ed.setInstanceProperties(o.id, props);
        } catch (e) {
          hooks.toast?.((e as Error).message);
        }
      }
    } else if (o.op === 'resetOverrides') {
      if (doc.get(o.id)?.type === 'INSTANCE') ed.resetOverrides(o.id);
    } else if (o.op === 'swap') {
      if (doc.get(o.id)?.type === 'INSTANCE' && doc.get(o.componentId)?.type === 'COMPONENT') swapInstance(ed, o.id, o.componentId);
    } else if (o.op === 'clone') {
      if (!doc.has(o.id)) continue;
      // copy the subtree; the root keeps the id the plugin already holds
      const map = new Map<NodeId, NodeId>();
      const created: Op[] = [];
      doc.walk(o.id, (n) => {
        const id = n.id === o.id ? o.newId : newId();
        map.set(n.id, id);
        const parent = n.id === o.id ? n.parent! : map.get(n.parent!)!;
        created.push({ op: 'create', node: { ...structuredClone(n), id, parent, index: n.id === o.id ? doc.indexAt(parent) : n.index } });
      });
      ed.applyOps(created);
    }
  }
}

/** A vector's path moved to start at (0, 0), with the box of its curves (not its control points). */
export function fitVectorPath(path: string): Partial<NodeData> {
  const cmds = parseSvgPath(path);
  let x0 = Infinity,
    y0 = Infinity,
    x1 = -Infinity,
    y1 = -Infinity;
  for (const sp of flattenPath(cmds, 0.02)) for (const p of sp.points) (x0 = Math.min(x0, p.x)), (y0 = Math.min(y0, p.y)), (x1 = Math.max(x1, p.x)), (y1 = Math.max(y1, p.y));
  if (!Number.isFinite(x0)) return { path };
  return { path: x0 || y0 ? pathToSvg(transformPath(cmds, [1, 0, 0, 1, -x0, -y0])) : path, width: Math.max(0.01, x1 - x0), height: Math.max(0.01, y1 - y0) };
}
